import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'components/modal/Modal';
import { RootState } from 'store/store';
import { setProformaAutomotorModalActive } from 'store/slices/proformaAutomotorModal.slice';
import Spinner from 'components/spinner/Spinner';
import './CustomerOrder.scss';
import { useTranslation } from 'react-i18next';
import IconItem from 'components/Item/IconItem/IconItem';
import { faCheck, faClose } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CustomerOrderModal from './CustomerOrderModal/CustomerOrderModal';
import CustomerOrderReferenceReplace from './CustomerOrderReferenceReplace/CustomerOrderReferenceReplace';
import ValidationReferenceNotFoundModal from './ValidationReferenceNotFoundModal/ValidationReferenceNotFoundModal';
import ValidationReferencesNotFoundComment from './ValidationReferencesNotFoundComment/ValidationReferencesNotFoundComment';
import ValidationReferencesPriceNotOK from './ValidationReferencesPriceNotOK/ValidationReferencesPriceNotOK';
import ValidationReferencesPriceNotOKComment from './ValidationReferencesPriceNotOKComment/ValidationReferencesPriceNotOKComment';
import ValidationReferencesFranco from './ValidationReferencesFranco/ValidationReferencesFranco';
import ValidationReferencesFrancoComment from './ValidationReferencesFrancoComment/ValidationReferencesFrancoComment';
import SubmitOrder from './SubmitOrder/SubmitOrder';
import ValidationReferencesMOQ from './ValidationReferencesMOQ/ValidationReferencesMOQ';
import ValidationReferencesMOQComment from './ValidationReferencesMOQComment/ValidationReferencesMOQComment';
import ValidationReferencesDuplicateDetected from './ValidationReferencesDuplicateDetected/ValidationReferencesDuplicateDetected';
import ValidationReferencesResultInfo from './ValidationReferencesResultInfo/ValidationReferencesResultInfo';
import ValidationReferencesResultInfoComment from './ValidationReferencesResultInfoComment/ValidationReferencesResultInfoComment';
import useHttp from 'utils/hooks/useHttp';
import { useSearchParams } from 'react-router-dom';
import ValidationReferencesSummary from './ValidationReferencesSummary/ValidationReferencesSummary';
import ValidationReferencesMultipleProviders from './ValidationReferencesSectionMultipleProviders/ValidationReferencesMultipleProviders';


type Props = {};

const CustomerOrder: React.FC<Props> = () => {
  const { proformaAutomotorModalActive } = useSelector((state: RootState) => state.proformaAutomotorModal);
  const [openModal, setOpenModal] = useState<boolean>(proformaAutomotorModalActive);
  const [stepModal, setStepModal] = useState<string | null>(null);
  const [stepModalNext, setStepModalNext] = useState<string>('');
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const proformaFile = JSON.parse(localStorage.getItem('proformaFileInProgress') as string);
  const http = useHttp();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {

    // Dacă modalul nu este deschis, nu facem nimic
    if (!openModal) { return; }

    if (stepModal == null || stepModal == '' || stepModal == 'CustomerOrderModal' || stepModal == 'SubmitOrder' || stepModal == 'Default') {
      return;
    }

    // Apel imediat
    saveDraft();
    // Apelează repetitiv la fiecare 10 secunde
    const interval = setInterval(() => {
      saveDraft();
    }, 10000);

    // Curăță intervalul când modalul se închide
    return () => clearInterval(interval);
  }, [stepModal]);


  const saveDraft = () => {
    if (!proformaFile) {
      return;
    }

    http.proforma.saveUploadProformaDraft(proformaFile);
  };


  useEffect(() => {
    setOpenModal(proformaAutomotorModalActive);
  }, [proformaAutomotorModalActive]);

  useEffect(() => {
    if (!openModal) {
      if (stepModal !== 'Default' && stepModal !== 'CustomerOrderModal') {
        saveDraft();
      }

      setStepModal(null);
      setStepModalNext('');
      setIsLoading(false);
      dispatch(setProformaAutomotorModalActive(false));
      localStorage.removeItem('proformaFileInProgress');

      searchParams.delete('order_start');
      searchParams.delete('draft');
      setSearchParams(searchParams);
    }

    if (openModal) {
      setStepModal('');
    }


  }, [openModal]);


  useEffect(() => {

    if (stepModal == 'CustomerOrderModal' || stepModal == 'SubmitOrder' || stepModal == 'Default' || stepModal == 'ValidationReferencesResultInfo') {
      setStepModalNext(stepModal);
      setIsLoading(false);

      return;
    }


    if (stepModal === '' && proformaFile && proformaFile.count > 0) {
      const isDraft = searchParams.get('draft');

      if (isDraft === 'true') {
        setStepModal('ValidationReferencesDuplicateDetected');
      } else {
        setStepModal('CustomerOrderModal');
      }
    }

    setIsLoading(true);


    if (stepModal == 'ValidationReferencesDuplicateDetected') {
      const referenceDuplicated = proformaFile.duplicated_rows;

      if (referenceDuplicated.length > 0) {
        setStepModalNext(stepModal);
      } else {
        setStepModal('ValidationReferencesMultipleProviders');
      }

    }

    if (stepModal == 'ValidationReferencesMultipleProviders') {
      const referenceMultipleProviders = proformaFile?.rows?.filter((row) => row.section === 'REFERENCE_MULTIPLE_PROVIDERS') || [];

      if (referenceMultipleProviders.length > 0) {
        setStepModalNext(stepModal);
      } else {
        setStepModal('CustomerOrderReferenceReplace');
      }

    }


    if (stepModal == 'CustomerOrderReferenceReplace') {
      const data = proformaFile.partsWithReplacements || [];
      const referenceReplace = data.filter((item) => item.parts?.filter(part => (part.replace_status === true && part.accepted_replace == undefined)).length > 0);


      if (referenceReplace.length > 0) {
        setStepModalNext(stepModal);
      } else {
        setStepModal('ValidationReferencesNotFound');
      }

    }


    if (stepModal == 'ValidationReferencesNotFound') {

      const referenceNotFound = proformaFile?.rows?.filter((row) => row.section === 'REFERENCE_NOT_FOUND');

      if (referenceNotFound.length > 0) {
        setStepModalNext(stepModal);
      } else {
        setStepModal('ValidationReferencesPriceNotOK');
      }

    }

    if (stepModal == 'ValidationReferencesPriceNotOK') {
      const referencePriceUpdate = proformaFile?.rows?.filter((row) => row.section === 'PRICE_UPDATE') || [];

      if (referencePriceUpdate.length > 0) {
        setStepModalNext(stepModal);
      } else {
        setStepModal('ValidationReferencesMOQ');
      }

    }

    if (stepModal == 'ValidationReferencesMOQ') {
      const data = proformaFile.partsWithReplacements || [];
      const minQuantityFiltered = data.filter((item) => item.parts.filter((part) => part.min_quantity != null && part.min_quantity > part.quantity).length > 0);


      if (minQuantityFiltered.length > 0) {
        setStepModalNext(stepModal);
      } else {
        setStepModal('ValidationReferencesFranco');
      }

    }

    if (stepModal && ['ValidationReferencesPriceNotOKComment', 'ValidationReferencesNotFoundComment', 'ValidationReferencesFrancoComment', 'ValidationReferencesMOQComment', 'ValidationReferencesResultInfoComment', 'ValidationReferencesSummary'].includes(stepModal)) {

      setStepModalNext(stepModal);

    }


    if (stepModal == 'ValidationReferencesFranco') {
      const data = proformaFile.partsWithReplacements || [];
      const francoNotValidatedFiltered = data.filter((item) => item.franco !== undefined && item.franco !== null && item.franco !== '');

      const francoNotValidated = francoNotValidatedFiltered.filter((item) => {
        const franco = item.franco || 0;
        const totalPrice = item.parts.map((part) => +part.total).reduce((acc: number, price: number) => acc + price, 0);

        return franco > totalPrice;
      });

      if (francoNotValidated.length > 0) {
        setStepModalNext(stepModal);
      } else {
        setStepModalNext('ValidationReferencesResultInfo');
      }

    }


    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 800);

    return () => clearTimeout(timeout);
  }, [stepModal]);


  const modalComponent = () => {

    if (isLoading) {
      return <div className="spiner-container-modal-order"><Spinner class="small" /></div>;
    }


    switch (stepModalNext) {
      case 'CustomerOrderModal':
        return <CustomerOrderModal setOpenModal={setOpenModal} setStepModal={setStepModal} />;
      case 'ValidationReferencesDuplicateDetected':
        return <ValidationReferencesDuplicateDetected setOpenModal={setOpenModal} setStepModal={setStepModal} stepModalNext={stepModalNext} />;

      case 'ValidationReferencesMultipleProviders':
        return <ValidationReferencesMultipleProviders setOpenModal={setOpenModal} setStepModal={setStepModal} stepModalNext={stepModalNext} />;
      case 'CustomerOrderReferenceReplace':
        return <CustomerOrderReferenceReplace setOpenModal={setOpenModal} setStepModal={setStepModal} stepModalNext={stepModalNext} />;
      case 'ValidationReferencesNotFound':
        return <ValidationReferenceNotFoundModal setOpenModal={setOpenModal} setStepModal={setStepModal} stepModalNext={stepModalNext} />;
      case 'ValidationReferencesNotFoundComment':
        return <ValidationReferencesNotFoundComment setOpenModal={setOpenModal} setStepModal={setStepModal} />;
      case 'ValidationReferencesPriceNotOK':
        return <ValidationReferencesPriceNotOK setOpenModal={setOpenModal} setStepModal={setStepModal} stepModalNext={stepModalNext} />;
      case 'ValidationReferencesPriceNotOKComment':
        return <ValidationReferencesPriceNotOKComment setOpenModal={setOpenModal} setStepModal={setStepModal} />;
      case 'ValidationReferencesMOQ':
        return <ValidationReferencesMOQ setOpenModal={setOpenModal} setStepModal={setStepModal} stepModalNext={stepModalNext} />;
      case 'ValidationReferencesMOQComment':
        return <ValidationReferencesMOQComment setOpenModal={setOpenModal} setStepModal={setStepModal} />;
      case 'ValidationReferencesFranco':
        return <ValidationReferencesFranco setOpenModal={setOpenModal} setStepModal={setStepModal} stepModalNext={stepModalNext} />;
      case 'ValidationReferencesFrancoComment':
        return <ValidationReferencesFrancoComment setOpenModal={setOpenModal} setStepModal={setStepModal} />;
      case 'ValidationReferencesResultInfo':
        return <ValidationReferencesResultInfo setOpenModal={setOpenModal} setStepModal={setStepModal} stepModalNext={stepModalNext} />;
      case 'ValidationReferencesResultInfoComment':
        return <ValidationReferencesResultInfoComment setOpenModal={setOpenModal} setStepModal={setStepModal} />;
      case 'ValidationReferencesSummary':
        return <ValidationReferencesSummary setOpenModal={setOpenModal} setStepModal={setStepModal} stepModalNext={stepModalNext} />;
      case 'SubmitOrder':
        return <SubmitOrder setOpenModal={setOpenModal} setStepModal={setStepModal} stepModalNext={stepModalNext} />;
      case 'Default':
        return (
          <div className="customer-order">
            <div className="close upload_order_modal_info_close">
              <IconItem icon={faClose} onClick={() => { setOpenModal(false); }} />
            </div>
            <div className="validation-customer-body">
              <div className="validation-customer-row">
                <div className="confirmation-container">
                  <div className="confirmation-icon">
                    <div className="circle-1">
                      <div className="circle-2">
                        <div className="circle-3">
                          <i className="checkmark"><FontAwesomeIcon icon={faCheck} /></i>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="confirmation-title">
                    {t('_order_sent_title')}
                  </div>
                  <div className="confirmation-message">
                    {t('_order_sent_message')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return <div>{t('_something_happened')}</div>;

    }
  };

  return (

    <Modal
      openModal={openModal}
      setOpenModal={setOpenModal}
      shouldCloseOnOverlayClick={false}
      childrenComponent={modalComponent()}
    />
  );
};

CustomerOrder.displayName = 'CustomerOrder';

export default CustomerOrder;